import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Shield, Info, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'

export default function ResourceMode() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [verificationMode, setVerificationMode] = useState<'verified' | 'application' | 'unverified'>('verified')
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      setVerificationMode(resource.verification_mode || 'verified')
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  const handleSave = async () => {
    if (!resource) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: {
          verification_mode: verificationMode,
        },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      hideBackButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update verification mode:', error)
      setError('Ошибка при сохранении режима проверки')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить режим', onSave, '#5288c1', '#ffffff')
    showBackButton(() => {
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`)
    })

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      hideBackButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, showBackButton, hideBackButton, setMainButtonLoading, navigate, resourceId])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  const allModes = [
    {
      value: 'verified' as const,
      emoji: '✅',
      title: 'С проверкой',
      description: 'Пользователи должны подтвердить подписку через бота',
    },
    {
      value: 'application' as const,
      emoji: '📝',
      title: 'По заявкам',
      description: 'Пользователи подают заявки, вы их одобряете',
    },
    {
      value: 'unverified' as const,
      emoji: '⚡',
      title: 'Без проверки',
      description: 'Пользователи получают награду без проверки',
    },
  ]

  // Filter out 'application' mode for bot type resources
  const modes = resource.resource_type === 'bot'
    ? allModes.filter(mode => mode.value !== 'application')
    : allModes

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Режим проверки</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Mode Selection */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Выберите режим</h2>
            <p className="text-xs text-tg-hint mt-1">Как будет проверяться подписка</p>
          </div>

          <div className="p-4 space-y-2">
            {modes.map((mode) => (
              <button
                key={mode.value}
                onClick={() => {
                  hapticFeedback('light')
                  setVerificationMode(mode.value)
                  setError('')
                }}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  verificationMode === mode.value
                    ? 'border-sky-500 bg-sky-500/10'
                    : 'border-transparent bg-tg-bg hover:border-tg-hint/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{mode.emoji}</div>
                  <div className="flex-1">
                    <div className="font-medium text-tg-text">{mode.title}</div>
                    <div className="text-xs text-tg-hint mt-1">{mode.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">О режимах проверки</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
